/**
 * useSubjectMastery Hook
 * Aggregates profile_beliefs by subject for the Subject Mastery widget.
 * Groups strength/weakness beliefs per subject slug, computes mastery %.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { CACHE_DURATIONS } from '@/lib/cache-config';
import { learningProfileKeys } from './use-learning-profile';
import { slugToName } from '@/lib/subject-mapping';

export interface SubjectMastery {
  slug: string;
  name: string;
  strengths: number;
  weaknesses: number;
  totalEvidence: number;
  /** 0–100 mastery score derived from strength vs weakness confidence */
  mastery: number;
  /** Subcategory details for expanded view */
  details: SubjectDetail[];
}

export interface SubjectDetail {
  subcategory: string;
  beliefType: 'weakness' | 'strength';
  confidence: number;
  evidenceCount: number;
}

interface RawBelief {
  belief_type: string;
  category: string;
  subcategory: string | null;
  confidence: number;
  evidence_count: number;
}

export function useSubjectMastery() {
  const { user } = useAuth();

  return useQuery({
    queryKey: [...learningProfileKeys.beliefs(user?.id ?? ''), 'subject-mastery'],
    queryFn: async (): Promise<SubjectMastery[]> => {
      if (!user?.id) return [];

      const { data: profile } = await supabase
        .from('learning_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) return [];

      const { data, error } = await supabase
        .from('profile_beliefs')
        .select('belief_type, category, subcategory, confidence, evidence_count')
        .eq('profile_id', profile.id)
        .in('belief_type', ['weakness', 'strength'])
        .order('confidence', { ascending: false });

      if (error) throw error;
      if (!data || data.length === 0) return [];

      // Group by category (subject slug)
      const grouped = new Map<string, RawBelief[]>();
      for (const row of data as RawBelief[]) {
        // Skip non-subject categories
        if (row.category === 'Writing Pattern') continue;

        const existing = grouped.get(row.category) || [];
        existing.push(row);
        grouped.set(row.category, existing);
      }

      // Compute per-subject mastery
      const subjects: SubjectMastery[] = [];
      for (const [slug, beliefs] of grouped) {
        let strengthSum = 0;
        let weaknessSum = 0;
        let strengthCount = 0;
        let weaknessCount = 0;
        let totalEvidence = 0;
        const details: SubjectDetail[] = [];

        for (const b of beliefs) {
          totalEvidence += b.evidence_count;
          // Skip "Conclusory Statements" from per-subject mastery
          // (it's a writing pattern, not substantive knowledge)
          if (b.subcategory === 'Conclusory Statements') continue;

          if (b.belief_type === 'strength') {
            strengthSum += b.confidence;
            strengthCount++;
          } else {
            weaknessSum += b.confidence;
            weaknessCount++;
          }
          details.push({
            subcategory: b.subcategory || slug,
            beliefType: b.belief_type as 'weakness' | 'strength',
            confidence: b.confidence,
            evidenceCount: b.evidence_count,
          });
        }

        // Mastery: ratio of strength confidence to total
        let mastery: number;
        if (strengthCount === 0 && weaknessCount === 0) {
          // Only "Conclusory Statements" — skip this subject
          continue;
        } else if (weaknessCount === 0) {
          mastery = Math.min(90, Math.round((strengthSum / strengthCount) * 100));
        } else if (strengthCount === 0) {
          mastery = Math.max(10, Math.round(30 - weaknessSum / weaknessCount * 20));
        } else {
          mastery = Math.round((strengthSum / (strengthSum + weaknessSum)) * 100);
        }

        subjects.push({
          slug,
          name: slugToName(slug) || slug,
          strengths: strengthCount,
          weaknesses: weaknessCount,
          totalEvidence,
          mastery: Math.max(0, Math.min(100, mastery)),
          details: details.sort((a, b) => b.confidence - a.confidence),
        });
      }

      // Sort by total evidence (most data first)
      return subjects.sort((a, b) => b.totalEvidence - a.totalEvidence);
    },
    enabled: !!user?.id,
    staleTime: CACHE_DURATIONS.USER_PROGRESS,
    gcTime: 10 * 60 * 1000,
  });
}
